import { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { Plus, Package, ShoppingBag, LogOut, MessageSquare, Phone, Globe, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewForm from "@/components/ReviewForm";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const Dashboard = () => {
  const { user, loading, roles, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [myListings, setMyListings] = useState<any[]>([]);
  const [allListings, setAllListings] = useState<any[]>([]);
  const [myOrders, setMyOrders] = useState<any[]>([]);
  const [sellerProfiles, setSellerProfiles] = useState<Record<string, any>>({});
  const [existingReviews, setExistingReviews] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<"listings" | "orders" | "all">("orders");

  const fetchData = async () => {
    if (!user) return;

    const { data: profileData } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", user.id)
      .single();
    setProfile(profileData);

    const { data: listings } = await supabase
      .from("listings")
      .select("*")
      .eq("seller_id", user.id)
      .order("created_at", { ascending: false });
    setMyListings(listings ?? []);

    // Admin: fetch all listings
    if (roles.includes("admin")) {
      const { data: all } = await supabase
        .from("listings")
        .select("*")
        .order("created_at", { ascending: false });
      setAllListings(all ?? []);
    }

    const { data: orders } = await supabase
      .from("orders")
      .select("*, listings(title, species, image_urls)")
      .or(`buyer_id.eq.${user.id},seller_id.eq.${user.id}`)
      .order("created_at", { ascending: false });
    setMyOrders(orders ?? []);

    // Fetch seller profiles for paid orders (buyer can see seller contact)
    if (orders) {
      const paidOrders = orders.filter(
        (o) => o.buyer_id === user.id && (o.status === "paid" || o.status === "completed")
      );
      const sellerIds = [...new Set(paidOrders.map((o) => o.seller_id))];
      const profiles: Record<string, any> = {};
      for (const sid of sellerIds) {
        const { data: sp } = await supabase
          .from("profiles")
          .select("full_name, phone, location")
          .eq("user_id", sid)
          .single();
        if (sp) profiles[sid] = sp;
      }
      setSellerProfiles(profiles);
    }

    // Check existing reviews
    const { data: reviews } = await supabase
      .from("reviews")
      .select("order_id")
      .eq("reviewer_id", user.id);
    setExistingReviews(new Set(reviews?.map((r: any) => r.order_id) ?? []));
  };

  useEffect(() => {
    if (user) fetchData();
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  const isSeller = roles.includes("seller");
  const isAdmin = roles.includes("admin");

  const handleContactSeller = async (order: any) => {
    // Check if conversation already exists (either direction)
    const { data: existing } = await supabase
      .from("messages")
      .select("id")
      .eq("listing_id", order.listing_id)
      .or(`and(sender_id.eq.${user!.id},receiver_id.eq.${order.seller_id}),and(sender_id.eq.${order.seller_id},receiver_id.eq.${user!.id})`)
      .limit(1);

    if (!existing || existing.length === 0) {
      // Fallback: create the conversation now
      const listingTitle = (order as any).listings?.title || "votre bois";
      await supabase.from("messages").insert({
        listing_id: order.listing_id,
        sender_id: user!.id,
        receiver_id: order.seller_id,
        content: `✅ Réservation confirmée ! L'acompte de 10€ a été payé. Vous pouvez maintenant discuter pour fixer le rendez-vous. Rappel : le solde restant est à payer directement au vendeur.`,
      });
    }

    navigate("/messages");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="container py-8 flex-1">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold">Tableau de bord</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Bienvenue, {profile?.full_name || user.email}
            </p>
          </div>
          <div className="flex gap-2">
            {isSeller && (
              <Button asChild className="gap-2">
                <Link to="/vendre"><Plus className="h-4 w-4" /> Nouvelle annonce</Link>
              </Button>
            )}
            {!isSeller && (
              <Button variant="outline" className="gap-2" onClick={async () => {
                await supabase.from("user_roles").insert({ user_id: user.id, role: "seller" as any });
                window.location.reload();
              }}>
                Devenir vendeur
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-muted rounded-lg p-1 w-fit">
          <button
            onClick={() => setTab("orders")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "orders" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}
          >
            <ShoppingBag className="h-4 w-4 inline mr-1.5" />
            Mes transactions
          </button>
          {isSeller && (
            <button
              onClick={() => setTab("listings")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "listings" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}
            >
              <Package className="h-4 w-4 inline mr-1.5" />
              Mes annonces
            </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setTab("all")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "all" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`}
              >
                <Globe className="h-4 w-4 inline mr-1.5" />
                Toutes les annonces
              </button>
            )}
          </div>

        {/* Orders tab */}
        {tab === "orders" && (
          <div className="space-y-4">
            {myOrders.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <ShoppingBag className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="font-display text-lg">Aucune transaction pour le moment</p>
                <p className="text-sm mt-1">Parcourez les annonces pour trouver votre bois.</p>
                <Button asChild className="mt-4" variant="outline">
                  <Link to="/annonces">Voir les annonces</Link>
                </Button>
              </div>
            ) : (
              myOrders.map((order) => {
                const isPaid = order.status === "paid" || order.status === "completed";
                const isBuyer = order.buyer_id === user.id;
                const sellerInfo = sellerProfiles[order.seller_id];

                return (
                  <div key={order.id} className="bg-card rounded-lg border p-4 space-y-3">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{(order as any).listings?.title ?? "Annonce"}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {order.volume} stères · Total bois: {order.total_wood_price}€ · Solde à payer: {(order.total_wood_price - order.commission_amount)}€
                        </p>
                      </div>
                      {isPaid && isBuyer ? (
                        <Button
                          size="sm"
                          className="gap-2"
                          onClick={() => handleContactSeller(order)}
                        >
                          <MessageSquare className="h-4 w-4" /> Contacter le vendeur
                        </Button>
                      ) : (
                        <Badge variant={order.status === "completed" ? "default" : order.status === "paid" ? "default" : "secondary"}>
                          {order.status === "completed" ? "Terminé" : order.status === "paid" ? "Payé" : "En attente"}
                        </Badge>
                      )}
                      {order.wood_pass_code && (
                        <Badge variant="outline" className="font-mono text-xs">
                          WP-{order.wood_pass_code.slice(0, 8)}
                        </Badge>
                      )}
                    </div>

                    {/* Seller contact info for paid orders */}
                    {isPaid && isBuyer && sellerInfo && (
                      <div className="bg-primary/5 rounded-lg p-3 flex items-center gap-3 border border-primary/20">
                        <Phone className="h-4 w-4 text-primary shrink-0" />
                        <div className="text-sm">
                          <span className="font-medium">{sellerInfo.full_name}</span>
                          {sellerInfo.phone && (
                            <span className="ml-2">
                              — <a href={`tel:${sellerInfo.phone}`} className="text-primary font-medium hover:underline">{sellerInfo.phone}</a>
                            </span>
                          )}
                          {sellerInfo.location && (
                            <span className="text-muted-foreground ml-2">· {sellerInfo.location}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Review form for completed orders */}
                    {(isPaid || order.status === "completed") && order.buyer_id === user.id && !existingReviews.has(order.id) && (
                      <ReviewForm orderId={order.id} sellerId={order.seller_id} onSubmitted={fetchData} />
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Listings tab */}
        {tab === "listings" && isSeller && (
          <div className="space-y-4">
            {myListings.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="font-display text-lg">Aucune annonce publiée</p>
                <Button asChild className="mt-4">
                  <Link to="/vendre"><Plus className="h-4 w-4 mr-1" /> Publier ma première annonce</Link>
                </Button>
              </div>
            ) : (
              myListings.map((listing) => (
                <div key={listing.id} className="bg-card rounded-lg border p-4 flex items-center gap-4">
                  <Link to={`/annonce/${listing.id}`} className="flex items-center gap-4 flex-1 hover:opacity-80 transition-opacity">
                    {listing.image_urls?.[0] && (
                      <img src={listing.image_urls[0]} alt="" className="w-16 h-16 rounded-md object-cover" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{listing.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {listing.species} · {listing.price_per_stere}€/stère · Stock: {listing.stock ?? listing.volume} stères
                      </p>
                    </div>
                  </Link>
                  <Button variant="outline" size="sm" asChild className="gap-1 shrink-0">
                    <Link to={`/annonce/${listing.id}`}><Pencil className="h-3 w-3" /> Modifier</Link>
                  </Button>
                  <Badge variant={listing.status === "active" ? "default" : listing.status === "sold_out" ? "destructive" : "secondary"}>
                    {listing.status === "active" ? "Active" : listing.status === "sold_out" ? "Épuisé" : listing.status === "pending" ? "En attente" : "Inactive"}
                  </Badge>
                </div>
              ))
            )}
          </div>
        )}

        {/* All listings tab (admin) */}
        {tab === "all" && isAdmin && (
          <div className="space-y-4">
            {allListings.length === 0 ? (
              <p className="text-center py-16 text-muted-foreground">Aucune annonce sur le site.</p>
            ) : (
              allListings.map((listing) => (
                <div key={listing.id} className="bg-card rounded-lg border p-4 flex items-center gap-4">
                  <Link to={`/annonce/${listing.id}`} className="flex items-center gap-4 flex-1 hover:opacity-80 transition-opacity">
                    {listing.image_urls?.[0] && (
                      <img src={listing.image_urls[0]} alt="" className="w-16 h-16 rounded-md object-cover" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-sm">{listing.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {listing.species} · {listing.price_per_stere}€/stère · Stock: {listing.stock ?? listing.volume} stères · {listing.location}
                      </p>
                    </div>
                  </Link>
                  <Badge variant={listing.status === "active" ? "default" : listing.status === "sold_out" ? "destructive" : "secondary"}>
                    {listing.status === "active" ? "Active" : listing.status === "sold_out" ? "Épuisé" : listing.status === "pending" ? "En attente" : "Inactive"}
                  </Badge>
                </div>
              ))
            )}
          </div>
        )}

        <div className="mt-8 pt-8 border-t">
          <Button variant="ghost" className="gap-2 text-muted-foreground" onClick={signOut}>
            <LogOut className="h-4 w-4" /> Déconnexion
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;
